/**
 * Generic Objectives Building Block Tab
 *
 * Config-driven: works for all 4 Objectives BBs (Quarterly, 1-Year, 3-Year,
 * Peak Ambition). The only differences are period labels, API prefix, alignment
 * context source, and building-blocks key — all supplied via ObjectivesBBConfig.
 *
 * Extracted from QuarterlyObjectivesTab.tsx (the reference implementation).
 */

import { useState, useEffect, useCallback } from "react";
import { Button, Tag, Tooltip, Modal, DatePicker, Input, message as antdMessage, Radio } from "antd";
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Sparkles,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Users,
  Download,
  Maximize2,
  Trash2,
  RotateCcw,
  Plus,
  X,
  Check,
  Pencil,
  Target,
  FileText,
} from "lucide-react";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";
import { VoInput } from "components/VoInput/VoInput";
import { ConsensusBuilderBar, type CommandCenterConfig } from "components/ConsensusBuilder/ConsensusBuilderBar";
import { renderMarkdown } from "utils/renderMarkdown";
import { exportTarPdf } from "utils/tarPdfExport";
import { getCategoryStyle, getCategoryLabel } from "./BlockDetailView";
import type { AllBuildingBlocks, WMBT, VisibilityMap } from "../types";
import { isVisibleForPeriod, normalizeWmbtItems } from "../types";
import dayjs from "dayjs";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

// ── Config type ──────────────────────────────────────────────────────────────

export interface ObjectivesBBConfig {
  bbType: string;
  blockKey: keyof AllBuildingBlocks;
  apiPrefix: string;
  periodType: string;
  periodLabelFn: (periodKey: string) => string;
  alignmentBlockKey: keyof AllBuildingBlocks | null;
  alignmentLabel: string;
  bbLabel: string;
  periodNoun: string;
}

// ── Preset configs for all 4 BB types ────────────────────────────────────────

export const OBJECTIVES_BB_CONFIGS: Record<string, ObjectivesBBConfig> = {
  quarterlyObjectives: {
    bbType: "quarterly",
    blockKey: "quarterlyObjectives",
    apiPrefix: "quarterly",
    periodType: "nqo",
    periodLabelFn: (pk) => {
      const parts = pk.split("-Q");
      return parts.length === 2 ? `Q${parts[1]} ${parts[0]}` : pk;
    },
    alignmentBlockKey: "oneYearObjectives",
    alignmentLabel: "1-Year Objectives",
    bbLabel: "Quarterly Objectives",
    periodNoun: "quarter",
  },
  oneYearObjectives: {
    bbType: "annual",
    blockKey: "oneYearObjectives",
    apiPrefix: "annual",
    periodType: "annual_obj",
    periodLabelFn: (pk) => pk.replace("FY", "FY "),
    alignmentBlockKey: "threeYearObjectives",
    alignmentLabel: "3-Year Objectives",
    bbLabel: "1-Year Objectives",
    periodNoun: "fiscal year",
  },
  threeYearObjectives: {
    bbType: "three_year",
    blockKey: "threeYearObjectives",
    apiPrefix: "three_year",
    periodType: "three_year_obj",
    periodLabelFn: (pk) => {
      const m = pk.match(/^3Y-(\d{4})-(\d{4})$/);
      return m ? `${m[1]}–${m[2]}` : pk;
    },
    alignmentBlockKey: "peakAmbitionObjectives",
    alignmentLabel: "Peak Ambition Objectives",
    bbLabel: "3-Year Objectives",
    periodNoun: "3-year period",
  },
  peakAmbitionObjectives: {
    bbType: "peak_ambition",
    blockKey: "peakAmbitionObjectives",
    apiPrefix: "peak_ambition",
    periodType: "peak_ambition_obj",
    periodLabelFn: (pk) => pk.replace("PA-", "Target: "),
    alignmentBlockKey: null,
    alignmentLabel: "Peak Ambition, Vision & Mission",
    bbLabel: "Peak Ambition Objectives",
    periodNoun: "peak target period",
  },
};

// ── Shared types ─────────────────────────────────────────────────────────────

interface PeriodInfo {
  period_key: string;
  period_start: string;
  period_end: string;
  is_locked: boolean;
  is_current_quarter: boolean;
}

interface Contribution {
  id: string;
  sessionId: string;
  periodKey: string;
  contributorUserId: string;
  contributorName: string;
  rawNotes: string;
  proposedWmbt: WmbtProposal[];
  submittedAt: string | null;
  updatedAt: string | null;
}

interface WmbtProposal {
  category: string;
  label: string;
  measurements?: Array<{ label: string; value: string }>;
  byWhen?: string;
  // Building-block / version shape: the measurement target lives in `value`
  // (string) and the fuller objective statement in `categoryObjective`. These
  // must render so the objective shows as a package deal (category + objective
  // + measurement), not just a bare label.
  value?: string | null;
  categoryObjective?: string | null;
}

interface PeriodMeta {
  id: string;
  sessionId: string;
  periodKey: string;
  deadline: string | null;
  message: string;
  visibilityOpen: boolean;
  synthesisReleased: boolean;
  synthesisSummary: { reportId: string; generatedAt: string } | null;
  synthesisReports?: { reportId: string; generatedAt: string }[];
}

interface Synthesis {
  id: string;
  periodKey: string;
  reportMarkdown: string;
  proposedWmbt: WmbtProposal[];
  generatedAt: string;
}

interface LockedObjectives {
  periodKey: string;
  wmbt: WmbtProposal[];
  appliedAt: string | null;
  isLocked: boolean;
}

interface ObjectiveVersion {
  version: number;
  wmbt: WmbtProposal[];
  source: string;
  reportId?: string;
  createdAt: string;
  editedAt?: string;
}

// ── Props ────────────────────────────────────────────────────────────────────

export interface ObjectivesBBTabProps {
  config: ObjectivesBBConfig;
  sessionId: string;
  // Team consensus blocks for this roadmap.
  buildingBlocks: AllBuildingBlocks;
  // The current viewer's per-user (Solo) blocks. Used when this tab's
  // own Solo/Team toggle is set to Solo. Optional — falls back to team
  // when missing (legacy data with no viewerOutput).
  soloBuildingBlocks?: AllBuildingBlocks;
  isAdvisorOrOwner?: boolean;
  currentUserId?: string;
  companyName?: string;
  viewMode?: "solo" | "team";
  onViewModeChange?: (v: "solo" | "team") => void;
  onSessionRefresh?: () => void;
  onReportCreated?: () => void;
  visibilityMap?: VisibilityMap;
  onVisibilityChange?: (updatedMap: VisibilityMap) => void;
  onLockChange?: () => void;
  autoEdit?: boolean;
  onSendToMainChat?: (message: string) => void;
  // State 2 gate: the team-only command-bar controls (visibility / notify /
  // synthesize) only render when the roadmap has 2+ participants. In State 1
  // (single-user), showViewToggle is false and the controls stay hidden.
  showViewToggle?: boolean;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function CategoryTag({ category }: { category: string }) {
  const style = getCategoryStyle(category);
  return (
    <Tag style={{ borderColor: style.border, color: style.text, background: style.bg, fontSize: 10, padding: "0 5px", lineHeight: "16px" }}>
      {getCategoryLabel(category)}
    </Tag>
  );
}

function CollapsibleSection({
  title,
  icon,
  defaultExpanded = true,
  variant = "default",
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  variant?: "default" | "accent" | "subtle";
  children: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const styles = {
    default: { container: "border border-slate-200 rounded-md bg-white", header: "hover:bg-slate-50", label: "text-foreground/80", icon: "text-foreground/50" },
    accent: { container: "border border-primary/20 rounded-md bg-primary/[0.03]", header: "hover:bg-primary/[0.06]", label: "text-primary/80", icon: "text-primary/50" },
    subtle: { container: "border border-slate-100 rounded-md bg-slate-50/40", header: "hover:bg-slate-50", label: "text-foreground/60", icon: "text-foreground/40" },
  }[variant];

  return (
    <div className={styles.container}>
      <button
        className={`w-full flex items-center justify-between bg-transparent border-0 cursor-pointer px-3 py-2 text-left transition-colors ${styles.header}`}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={`text-[11px] font-semibold ${styles.label} flex items-center gap-1.5`}>
          {icon && <span className={styles.icon}>{icon}</span>}
          {title}
        </span>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>
      {expanded && <div className="px-3 pb-2.5 border-t border-slate-100 pt-2">{children}</div>}
    </div>
  );
}

function ObjectiveCard({
  item,
  index,
  isEditing,
  onChange,
  onRemove,
}: {
  item: WmbtProposal;
  index: number;
  isEditing?: boolean;
  onChange?: (index: number, updated: WmbtProposal) => void;
  onRemove?: (index: number) => void;
}) {
  const style = getCategoryStyle(item.category);
  // Building-block / version items carry the measurement in a `value` string
  // (no `measurements[]` array). Fall back to it so the measurement always shows.
  const editMeasurements = item.measurements || [];
  const viewMeasurements = editMeasurements.length > 0
    ? editMeasurements
    : (item.value && item.value.trim() ? [{ label: "", value: item.value }] : []);
  return (
    <div className="rounded-lg border bg-white p-3 space-y-1.5" style={{ borderColor: style.border }}>
      <div className="flex items-start gap-2">
        {isEditing ? (
          <Input
            size="small"
            value={item.category}
            onChange={(e) => onChange?.(index, { ...item, category: e.target.value })}
            style={{ width: 100, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", borderColor: style.border, color: style.text, background: style.bg }}
            variant="outlined"
          />
        ) : (
          <CategoryTag category={item.category} />
        )}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <Input.TextArea
              value={item.label}
              onChange={(e) => onChange?.(index, { ...item, label: e.target.value })}
              autoSize={{ minRows: 1, maxRows: 3 }}
              variant="borderless"
              style={{ fontSize: 12, fontWeight: 500, padding: 0 }}
              placeholder="Objective statement"
            />
          ) : (
            <>
              <p className="text-xs font-medium leading-snug text-foreground/90">{item.label || item.categoryObjective}</p>
              {item.label && item.categoryObjective && item.categoryObjective.trim() && item.categoryObjective !== item.label && (
                <p className="text-[11px] leading-snug text-muted-foreground mt-0.5">{item.categoryObjective}</p>
              )}
            </>
          )}
        </div>
        {isEditing && onRemove && (
          <button
            onClick={() => onRemove(index)}
            className="p-0.5 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors bg-transparent border-0 cursor-pointer shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      {(isEditing || viewMeasurements.length > 0) && (
        <div className="ml-0 space-y-1">
          {(isEditing ? editMeasurements : viewMeasurements).map((m, mi) => (
            <div key={mi} className="flex items-center gap-1.5">
              {isEditing ? (
                <>
                  <Input
                    size="small"
                    value={m.label}
                    onChange={(e) => {
                      const newM = [...(item.measurements || [])];
                      newM[mi] = { ...newM[mi], label: e.target.value };
                      onChange?.(index, { ...item, measurements: newM });
                    }}
                    placeholder="Metric"
                    style={{ fontSize: 10, flex: 1 }}
                    variant="filled"
                  />
                  <span className="text-[10px] text-muted-foreground/50">→</span>
                  <Input
                    size="small"
                    value={m.value}
                    onChange={(e) => {
                      const newM = [...(item.measurements || [])];
                      newM[mi] = { ...newM[mi], value: e.target.value };
                      onChange?.(index, { ...item, measurements: newM });
                    }}
                    placeholder="Target"
                    style={{ fontSize: 10, flex: 1 }}
                    variant="filled"
                  />
                  <button
                    onClick={() => {
                      const newM = (item.measurements || []).filter((_, j) => j !== mi);
                      onChange?.(index, { ...item, measurements: newM });
                    }}
                    className="p-0.5 text-slate-300 hover:text-red-400 bg-transparent border-0 cursor-pointer"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </>
              ) : (
                <span className="inline-flex items-center gap-1 bg-slate-100/70 rounded px-2 py-0.5 text-[10px]">
                  {m.label && <span className="text-muted-foreground">{m.label}</span>}
                  {m.value && <>{m.label && <span className="text-muted-foreground/50">→</span>}<span className="font-medium">{m.value}</span></>}
                </span>
              )}
            </div>
          ))}
          {isEditing && (
            <button
              onClick={() => onChange?.(index, { ...item, measurements: [...(item.measurements || []), { label: "", value: "" }] })}
              className="text-[10px] text-primary/60 hover:text-primary flex items-center gap-0.5 bg-transparent border-0 cursor-pointer p-0"
            >
              <Plus className="h-2.5 w-2.5" /> Add measurement
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AlignmentContext({ items, label }: { items: WMBT[]; label: string }) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return null;

  type ObjGroup = { item: WMBT; measurements: Array<{ label: string; value: string }> };
  const seenKeys = new Set<string>();
  const groups: ObjGroup[] = [];
  items.forEach((item) => {
    const catObj = item.categoryObjective?.trim() ?? null;
    if (catObj) {
      if (!seenKeys.has(catObj)) {
        seenKeys.add(catObj);
        groups.push({ item: { ...item }, measurements: [] });
      }
    } else if (item.label?.trim()) {
      const parentRow = groups[groups.length - 1];
      if (parentRow) {
        parentRow.measurements.push({ label: item.label || "", value: (item as any).value || "" });
      }
    }
  });

  if (groups.length === 0) return null;

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 mb-3">
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-left bg-transparent border-0 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
          <Eye className="h-3 w-3" />
          {label} — context for alignment
        </span>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-3 pb-2.5 space-y-1.5 border-t border-slate-100 pt-2">
          {groups.map(({ item, measurements }, i) => (
            <div key={i} className="flex items-start gap-2">
              <CategoryTag category={item.category} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-snug">{item.categoryObjective}</p>
                {measurements.length > 0 && (
                  <p className="text-[10px] text-muted-foreground/60 truncate mt-0.5">
                    {measurements.map((m) => `${m.label}${m.value ? ` → ${m.value}` : ""}`).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MyContributionForm({
  config,
  sessionId,
  periodKey,
  onSaved,
  onSendToMainChat,
}: {
  config: ObjectivesBBConfig;
  sessionId: string;
  periodKey: string;
  contribution?: Contribution | null;
  fallbackWmbt?: WMBT[];
  onSaved: (c: Contribution) => void;
  autoEdit?: boolean;
  onSendToMainChat?: (message: string) => void;
}) {
  const [rawNotes, setRawNotes] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const { isRecording, isInitializing, isProcessingAudio, startRecording, stopRecording } =
    useMicRecorder();

  const handleSubmit = useCallback(async (overrideText?: string) => {
    const text = overrideText ?? rawNotes;
    if (!text.trim() || isParsing) return;
    if (onSendToMainChat) {
      const periodLabel = config.periodLabelFn(periodKey);
      onSendToMainChat(`Update my ${config.bbLabel} for ${periodLabel}: ${text}`);
      setRawNotes("");
      return;
    }
    if (!periodKey) return;
    setIsParsing(true);
    try {
      const url = `${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/contributions/parse-and-save`;
      const r = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period_key: periodKey, raw_notes: text }),
      });
      if (r.ok) {
        const saved: Contribution = await r.json();
        if (saved.proposedWmbt?.length === 0) {
          antdMessage.warning("Vo saved your notes but couldn't structure them into objectives — try adding more detail about goals and targets.");
        }
        onSaved(saved);
      } else {
        antdMessage.error("Failed to save objectives — please try again.");
      }
    } catch (e) {
      console.error("[ObjBB] handleSubmit error", e);
      antdMessage.error("Network error saving objectives — please try again.");
    } finally {
      setIsParsing(false);
    }
  }, [rawNotes, isParsing, periodKey, sessionId, config.bbType, config.bbLabel, config.periodLabelFn, onSaved, onSendToMainChat]);

  return (
    <CollapsibleSection title="Work with Vo on My Objectives" icon={<Sparkles className="h-3 w-3" />} variant="accent" defaultExpanded>
      <VoInput
        value={rawNotes}
        onChange={setRawNotes}
        onSubmit={handleSubmit}
        isRecording={isRecording}
        isInitializing={isInitializing}
        isProcessingAudio={isProcessingAudio}
        isSubmitting={isParsing}
        placeholder={`Or type your proposed objectives for this ${config.periodNoun}...`}
        onStartRecording={() =>
          startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
            const combined = rawNotes ? `${rawNotes} ${transcript}` : transcript;
            setRawNotes(combined);
            await handleSubmit(combined);
          })
        }
        onStopRecording={stopRecording}
      />
    </CollapsibleSection>
  );
}

function TeamDraftEditForm({
  config,
  sessionId,
  periodKey,
  onSaved,
  onSendToMainChat,
}: {
  config: ObjectivesBBConfig;
  sessionId: string;
  periodKey: string;
  onSaved: (wmbt: WmbtProposal[]) => void;
  onSendToMainChat?: (message: string) => void;
}) {
  const [rawNotes, setRawNotes] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const { isRecording, isInitializing, isProcessingAudio, startRecording, stopRecording } =
    useMicRecorder();

  const handleSubmit = useCallback(async (overrideText?: string) => {
    const text = overrideText ?? rawNotes;
    if (!text.trim() || isParsing) return;
    if (onSendToMainChat) {
      const periodLabel = config.periodLabelFn(periodKey);
      onSendToMainChat(`Update the team ${config.bbLabel} for ${periodLabel}: ${text}`);
      setRawNotes("");
      return;
    }
    if (!periodKey) return;
    setIsParsing(true);
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/edit-draft/${periodKey}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ period_key: periodKey, raw_notes: text }),
        }
      );
      if (r.ok) {
        const data = await r.json();
        if (data.proposedWmbt?.length > 0) {
          onSaved(data.proposedWmbt);
          setRawNotes("");
        } else {
          antdMessage.warning("Vo couldn't structure your input into objectives — try adding more detail.");
        }
      } else {
        const err = await r.json().catch(() => ({}));
        antdMessage.error(err.detail || "Failed to save edits — please try again.");
      }
    } catch {
      antdMessage.error("Network error saving edits — please try again.");
    } finally {
      setIsParsing(false);
    }
  }, [rawNotes, isParsing, periodKey, sessionId, config.bbType, config.bbLabel, config.periodLabelFn, onSaved, onSendToMainChat]);

  return (
    <VoInput
      value={rawNotes}
      onChange={setRawNotes}
      onSubmit={handleSubmit}
      isRecording={isRecording}
      isInitializing={isInitializing}
      isProcessingAudio={isProcessingAudio}
      isSubmitting={isParsing}
      placeholder="Or type edits to team objectives..."
      onStartRecording={() =>
        startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
          const combined = rawNotes ? `${rawNotes} ${transcript}` : transcript;
          setRawNotes(combined);
          await handleSubmit(combined);
        })
      }
      onStopRecording={stopRecording}
    />
  );
}

function SynthesisSection({
  synthesis,
  bbLabel,
  periodLabel,
  onDownload,
  onDelete,
}: {
  synthesis: Synthesis;
  bbLabel: string;
  periodLabel?: string;
  onDownload?: () => void;
  onDelete?: () => void;
}) {
  const tarTitle = `Roadmap: ${periodLabel ? periodLabel + " " : ""}${bbLabel} Team Alignment Report`;
  const [expanded, setExpanded] = useState(false);
  const [focusOpen, setFocusOpen] = useState(false);

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer hover:bg-slate-100/60 transition-colors select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <FileText className="h-3 w-3 text-foreground/70 shrink-0" />
        <span className="text-[11px] font-semibold text-foreground/70 ml-1.5">{tarTitle}</span>
        <span className="text-[10px] text-muted-foreground ml-2 truncate min-w-0">
          {synthesis.generatedAt ? new Date(synthesis.generatedAt).toLocaleDateString() : ""}
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 shrink-0">
          {onDownload && (
            <Tooltip title="Download as PDF">
              <button
                onClick={(e) => { e.stopPropagation(); onDownload(); }}
                className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
              >
                <Download className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
          <Tooltip title="Focus mode">
            <button
              onClick={(e) => { e.stopPropagation(); setFocusOpen(true); }}
              className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
            >
              <Maximize2 className="h-3 w-3" />
            </button>
          </Tooltip>
          {onDelete && (
            <Tooltip title="Delete report">
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="flex items-center bg-transparent border-0 p-0 cursor-pointer text-slate-300 hover:text-red-500 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </Tooltip>
          )}
        </div>
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-2 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-2 shrink-0" />
        }
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-violet-100">
          <div className="text-xs text-foreground/80 leading-relaxed">
            {renderMarkdown(synthesis.reportMarkdown, { baseFontSize: 11 })}
          </div>
        </div>
      )}
      <Modal
        open={focusOpen}
        onCancel={() => setFocusOpen(false)}
        footer={null}
        width={720}
        title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-700" /> {tarTitle}</span>}
      >
        <div className="text-sm text-foreground/80 leading-relaxed max-h-[70vh] overflow-y-auto py-2">
          {renderMarkdown(synthesis.reportMarkdown, { baseFontSize: 13 })}
        </div>
      </Modal>
    </div>
  );
}

function NotifyModal({
  open,
  onClose,
  config,
  sessionId,
  periodKey,
}: {
  open: boolean;
  onClose: () => void;
  config: ObjectivesBBConfig;
  sessionId: string;
  periodKey: string;
}) {
  const [deadline, setDeadline] = useState<dayjs.Dayjs | null>(null);
  const [message, setMessage] = useState("Please submit your proposed objectives by the deadline above.");
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) { setSent(false); setEmailError(null); }
  }, [open]);

  const handleSend = async () => {
    const rawEmails = emails.split(/[\n,]+/).map((e) => e.trim()).filter(Boolean);
    if (!rawEmails.length) { setEmailError("Add at least one email address to notify."); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = rawEmails.filter((e) => !emailRegex.test(e));
    if (invalid.length) { setEmailError(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`); return; }
    setEmailError(null);
    setSending(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/rounds/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          period_key: periodKey,
          deadline: deadline?.toISOString() || null,
          message,
          participants: rawEmails.map((email) => ({ email })),
          send_notifications: true,
        }),
      });
      if (r.ok) { setSent(true); setTimeout(onClose, 1500); }
    } catch { /* ignore */ }
    finally { setSending(false); }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Notify Team — ${config.periodLabelFn(periodKey)} ${config.bbLabel}`}
      footer={sent ? null : [
        <Button key="cancel" onClick={onClose}>Cancel</Button>,
        <Button key="send" type="primary" loading={sending} onClick={handleSend}>Send Notification</Button>,
      ]}
    >
      {sent ? (
        <div className="py-6 text-center text-sm text-green-600 font-medium">Notifications sent!</div>
      ) : (
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Team emails <span className="text-red-500">*</span></label>
            <Input.TextArea value={emails} onChange={(e) => { setEmails(e.target.value); setEmailError(null); }} rows={3} placeholder="one@example.com, two@example.com" />
            {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Submission deadline (optional)</label>
            <DatePicker value={deadline} onChange={setDeadline} style={{ width: "100%" }} placeholder="No deadline" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Personal message</label>
            <Input.TextArea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
          </div>
        </div>
      )}
    </Modal>
  );
}

function ContributorList({ contributions }: { contributions: Contribution[] }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (contributions.length === 0) {
    return <p className="text-xs text-muted-foreground/60 italic text-center py-3">No contributions yet for this period.</p>;
  }
  return (
    <div className="space-y-2">
      {contributions.map((c) => (
        <div key={c.id} className="border border-slate-100 rounded-md overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-3 py-2 bg-transparent border-0 cursor-pointer text-left hover:bg-slate-50/50"
            onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}
          >
            <div>
              <span className="text-xs font-medium">{c.contributorName}</span>
              {c.proposedWmbt?.length > 0 && (
                <span className="text-[10px] text-muted-foreground ml-2">
                  {c.proposedWmbt.length} objective{c.proposedWmbt.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            {expandedId === c.id ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
          </button>
          {expandedId === c.id && (
            <div className="px-3 pb-3 border-t border-slate-100 pt-2 space-y-2">
              {c.rawNotes && (
                <div>
                  <p className="text-[10px] font-medium text-muted-foreground mb-1">Raw notes</p>
                  <p className="text-xs text-foreground/70 leading-relaxed">{c.rawNotes}</p>
                </div>
              )}
              {c.proposedWmbt?.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground">Proposed objectives</p>
                  {c.proposedWmbt.map((item, i) => <ObjectiveCard key={i} item={item} index={i} />)}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VersionedTeamObjectives({
  config,
  sessionId,
  periodKey,
  isAdvisorOrOwner,
  onSessionRefresh,
  onVersionChange,
  fallbackWmbt,
}: {
  config: ObjectivesBBConfig;
  sessionId: string;
  periodKey: string;
  isAdvisorOrOwner?: boolean;
  onSessionRefresh?: () => void;
  onVersionChange?: (v: number, total: number) => void;
  fallbackWmbt?: WmbtProposal[];
}) {
  const [versions, setVersions] = useState<ObjectiveVersion[]>([]);
  const [viewIndex, setViewIndex] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [editItems, setEditItems] = useState<WmbtProposal[]>([]);
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/versions/${periodKey}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        const v: ObjectiveVersion[] = data.versions || [];
        setVersions(v);
        if (v.length > 0) {
          const idx = v.length - 1;
          setViewIndex(idx);
          onVersionChange?.(idx + 1, v.length);
        }
      }
    } catch { /* ignore */ }
  }, [sessionId, config.bbType, periodKey, onVersionChange]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  const current = viewIndex >= 0 && viewIndex < versions.length ? versions[viewIndex] : null;
  const isLatest = viewIndex === versions.length - 1;
  const total = versions.length;

  const handleRestore = async () => {
    if (!current || isLatest) return;
    setRestoring(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/versions/${periodKey}/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ version: current.version }),
      });
      if (r.ok) {
        antdMessage.success(`Restored version ${current.version} as new version`);
        await loadVersions();
        onSessionRefresh?.();
      }
    } catch { antdMessage.error("Restore failed"); }
    finally { setRestoring(false); }
  };

  const startEdit = () => {
    const source = current?.wmbt || [];
    if (source.length === 0) return;
    setEditItems(source.map((item) => ({ ...item, measurements: (item.measurements || []).map((m) => ({ ...m })) })));
    setIsEditing(true);
  };

  const cancelEdit = () => { setIsEditing(false); setEditItems([]); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period_key: periodKey, objectives: editItems }),
      });
      if (r.ok) {
        antdMessage.success("Objectives saved");
        setIsEditing(false);
        setEditItems([]);
        await loadVersions();
        onSessionRefresh?.();
      }
    } catch { antdMessage.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addObjective = () => {
    setEditItems((prev) => [...prev, { category: "", label: "", measurements: [{ label: "", value: "" }] }]);
  };

  const handleChange = (index: number, updated: WmbtProposal) => {
    setEditItems((prev) => prev.map((item, i) => i === index ? updated : item));
  };

  const handleRemove = (index: number) => {
    setEditItems((prev) => prev.filter((_, i) => i !== index));
  };

  const sourceLabel = (src: string) => {
    if (src === "synthesis") return "Vo synthesis";
    if (src === "direct_edit") return "Edited with Vo";
    if (src === "manual") return "Manual edit";
    if (src.startsWith("restored_from_v")) return `Restored from v${src.replace("restored_from_v", "")}`;
    return src;
  };

  if (total === 0) {
    // No per-period team version history yet — fall back to the building-block
    // objectives (the same content the collapsed grid preview shows) so the
    // expanded view never appears emptier than the preview. Read-only.
    if (!fallbackWmbt || fallbackWmbt.length === 0) return null;
    return (
      <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
        <div className="flex items-center px-3 py-2">
          <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
            <Target className="h-3 w-3" />
            Team Objectives
          </span>
        </div>
        <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
          {fallbackWmbt.map((item, i) => (
            <ObjectiveCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    );
  }

  const displayItems = isEditing ? editItems : (current?.wmbt || []);

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer select-none"
        onClick={() => { if (!isEditing) setExpanded((v) => !v); }}
      >
        <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
          <Target className="h-3 w-3" />
          Team Objectives
        </span>
        <div className="flex-1" />
        {total > 0 && !isEditing && (
          <div className="flex items-center gap-1 text-[10px] text-foreground/60 mr-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex <= 0}
              onClick={() => { setViewIndex((v) => v - 1); }}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="font-medium tabular-nums">
              v{viewIndex + 1}/{total}
            </span>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex >= total - 1}
              onClick={() => { setViewIndex((v) => v + 1); }}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {!isLatest && !isEditing && isAdvisorOrOwner && (
          <Tooltip title={`Restore v${viewIndex + 1} as new version`}>
            <button onClick={(e) => { e.stopPropagation(); handleRestore(); }} disabled={restoring} className="p-1 rounded hover:bg-slate-200 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors mr-0.5">
              <RotateCcw className={`h-3 w-3 ${restoring ? "animate-spin" : ""}`} />
            </button>
          </Tooltip>
        )}
        {isAdvisorOrOwner && !isEditing && isLatest && (
          <Tooltip title="Edit objectives">
            <button onClick={(e) => { e.stopPropagation(); startEdit(); }} className="p-1 rounded hover:bg-slate-200 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors">
              <Pencil className="h-3 w-3" />
            </button>
          </Tooltip>
        )}
        {isEditing && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Button size="small" onClick={cancelEdit} style={{ fontSize: 10 }}>Cancel</Button>
            <Button size="small" type="primary" loading={saving} onClick={saveEdit} style={{ fontSize: 10 }} icon={<Check className="h-3 w-3" />}>Save</Button>
          </div>
        )}
        {!isEditing && (expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
        )}
      </div>
      {expanded && <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
        {current && !isEditing && (
          <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
            {sourceLabel(current.source)} — {new Date(current.createdAt).toLocaleString()}{current.editedAt && " · edited"}
          </p>
        )}
        {displayItems.map((item, i) => (
          <ObjectiveCard
            key={i}
            item={item}
            index={i}
            isEditing={isEditing}
            onChange={isEditing ? handleChange : undefined}
            onRemove={isEditing ? handleRemove : undefined}
          />
        ))}
        {isEditing && (
          <button
            onClick={addObjective}
            className="w-full py-2 rounded-lg border border-dashed border-slate-200 text-[11px] text-primary/60 hover:text-primary hover:border-primary/30 flex items-center justify-center gap-1 bg-transparent cursor-pointer transition-colors"
          >
            <Plus className="h-3 w-3" /> Add objective
          </button>
        )}
      </div>}
    </div>
  );
}

function VersionedSoloObjectives({
  config,
  sessionId,
  periodKey,
  onSessionRefresh,
  refreshKey,
  buildingBlocks,
  fallbackWmbt,
}: {
  config: ObjectivesBBConfig;
  sessionId: string;
  periodKey: string;
  onSessionRefresh?: () => void;
  refreshKey?: number;
  buildingBlocks?: AllBuildingBlocks;
  fallbackWmbt?: WmbtProposal[];
}) {
  const [versions, setVersions] = useState<ObjectiveVersion[]>([]);
  const [viewIndex, setViewIndex] = useState(-1);
  const [restoring, setRestoring] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const blockFingerprint = JSON.stringify(buildingBlocks?.[config.blockKey] ?? null);

  const loadVersions = useCallback(async () => {
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/solo/bb/${config.bbType}/versions/${periodKey}`, { credentials: "include" });
      if (r.ok) {
        const data = await r.json();
        const v: ObjectiveVersion[] = data.versions || [];
        setVersions(v);
        if (v.length > 0) setViewIndex(v.length - 1);
      }
    } catch { /* ignore */ }
  }, [sessionId, config.bbType, periodKey]);

  useEffect(() => { loadVersions(); }, [loadVersions, refreshKey, blockFingerprint]);

  const current = viewIndex >= 0 && viewIndex < versions.length ? versions[viewIndex] : null;
  const isLatest = viewIndex === versions.length - 1;
  const total = versions.length;

  const handleRestore = async () => {
    if (!current || isLatest) return;
    setRestoring(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/solo/bb/${config.bbType}/versions/${periodKey}/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ version: current.version }),
      });
      if (r.ok) {
        antdMessage.success(`Restored version ${current.version} as new version`);
        await loadVersions();
        onSessionRefresh?.();
      }
    } catch { antdMessage.error("Restore failed"); }
    finally { setRestoring(false); }
  };

  const sourceLabel = (src: string) => {
    if (src === "vo_chat") return "Vo chat";
    if (src === "manual_edit") return "Manual edit";
    if (src === "synthesis") return "Vo synthesis";
    if (src === "direct_edit") return "Edited with Vo";
    if (src.startsWith("restored_from_v")) return `Restored from v${src.replace("restored_from_v", "")}`;
    return src;
  };

  if (total === 0) {
    // No per-period solo version history yet — fall back to the building-block
    // objectives (the same content the collapsed grid preview shows) so the
    // expanded view never appears emptier than the preview. Read-only.
    if (!fallbackWmbt || fallbackWmbt.length === 0) return null;
    return (
      <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
        <div className="flex items-center px-3 py-2">
          <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
            <Target className="h-3 w-3" />
            My Objectives
          </span>
        </div>
        <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
          {fallbackWmbt.map((item, i) => (
            <ObjectiveCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-slate-100 rounded-md bg-slate-50/60 overflow-hidden">
      <div
        className="flex items-center px-3 py-2 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[11px] font-semibold text-foreground/70 flex items-center gap-1.5">
          <Target className="h-3 w-3" />
          My Objectives
        </span>
        <div className="flex-1" />
        {total > 0 && (
          <div className="flex items-center gap-1 text-[10px] text-foreground/60 mr-2" onClick={(e) => e.stopPropagation()}>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex <= 0}
              onClick={() => setViewIndex((v) => v - 1)}
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="font-medium tabular-nums">v{viewIndex + 1}/{total}</span>
            <button
              className="p-0.5 rounded hover:bg-slate-200 disabled:opacity-30 bg-transparent border-0 cursor-pointer"
              disabled={viewIndex >= total - 1}
              onClick={() => setViewIndex((v) => v + 1)}
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
        {!isLatest && (
          <Tooltip title={`Restore v${viewIndex + 1} as new version`}>
            <button onClick={(e) => { e.stopPropagation(); handleRestore(); }} disabled={restoring} className="p-1 rounded hover:bg-slate-200 bg-transparent border-0 cursor-pointer text-slate-400 hover:text-primary transition-colors mr-0.5">
              <RotateCcw className={`h-3 w-3 ${restoring ? "animate-spin" : ""}`} />
            </button>
          </Tooltip>
        )}
        {expanded
          ? <ChevronUp className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
          : <ChevronDown className="h-3 w-3 text-muted-foreground ml-1.5 shrink-0" />
        }
      </div>
      {expanded && <div className="px-3 py-2.5 space-y-2 border-t border-slate-100">
        {current && (
          <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
            {sourceLabel(current.source)} — {new Date(current.createdAt).toLocaleString()}{current.editedAt && " · edited"}
          </p>
        )}
        {(current?.wmbt || []).map((item, i) => (
          <ObjectiveCard key={i} item={item} index={i} />
        ))}
      </div>}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export function ObjectivesBBTab({
  config,
  sessionId,
  buildingBlocks,
  soloBuildingBlocks,
  isAdvisorOrOwner,
  currentUserId,
  companyName,
  viewMode: propViewMode,
  onSessionRefresh,
  onReportCreated,
  visibilityMap: parentVisMap,
  onVisibilityChange,
  onLockChange,
  autoEdit,
  onSendToMainChat,
  showViewToggle = false,
}: ObjectivesBBTabProps) {
  const [allPeriods, setAllPeriods] = useState<PeriodInfo[]>([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>("");
  const [isLocked, setIsLocked] = useState(false);
  const [lockedObjectives, setLockedObjectives] = useState<LockedObjectives | null>(null);
  const [myContribution, setMyContribution] = useState<Contribution | null>(null);
  const [allContributions, setAllContributions] = useState<Contribution[]>([]);
  const [periodMeta, setPeriodMeta] = useState<PeriodMeta | null>(null);
  const [synthesis, setSynthesis] = useState<Synthesis | null>(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [synthStream, setSynthStream] = useState("");
  const [synthError, setSynthError] = useState<string | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [synthConfirmOpen, setSynthConfirmOpen] = useState(false);
  const [committedOnly, setCommittedOnly] = useState(false);
  const [locking, setLocking] = useState(false);
  const [visMap, setVisMap] = useState<VisibilityMap>(parentVisMap ?? {});
  const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);

  useEffect(() => { if (parentVisMap) setVisMap(parentVisMap); }, [parentVisMap]);

  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setVisMap(data); })
      .catch(() => {});
  }, [sessionId]);

  // Load period list
  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/period-info`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (!d) return;
        setAllPeriods(d.allQuarters || []);
        if (d.activeQuarterKey) setSelectedPeriodKey(d.activeQuarterKey);
      })
      .catch(() => {});
  }, [sessionId, config.bbType]);

  // Load data when period changes
  // Fetch the Team Contributions list for the selected period. Extracted so we
  // can refetch when the viewer builds/commits their own objectives or after
  // synthesis — otherwise the mount-time list goes stale (a just-committed block
  // wouldn't appear until a remount).
  const loadContributions = useCallback(() => {
    if (!selectedPeriodKey) return;
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/contributions/${selectedPeriodKey}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((docs: Contribution[] | null) => {
        if (!docs) return;
        setAllContributions(docs);
        if (currentUserId) setMyContribution(docs.find((d) => d.contributorUserId === currentUserId) || null);
      })
      .catch(() => {});
  }, [sessionId, config.bbType, selectedPeriodKey, currentUserId]);

  // Refetch contributions when the viewer's OWN objectives block changes.
  const ownBlockFingerprint = JSON.stringify(soloBuildingBlocks?.[config.blockKey] ?? null);
  useEffect(() => { loadContributions(); }, [ownBlockFingerprint, loadContributions]);

  useEffect(() => {
    if (!selectedPeriodKey) return;
    const periodInfo = allPeriods.find((q) => q.period_key === selectedPeriodKey);
    const locked = periodInfo?.is_locked ?? false;
    setIsLocked(locked);
    setPeriodMeta(null);
    setSynthesis(null);
    setMyContribution(null);
    setAllContributions([]);
    setLockedObjectives(null);

    if (locked) {
      fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/objectives/${selectedPeriodKey}`, { credentials: "include" })
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d) setLockedObjectives(d); })
        .catch(() => {});
      return;
    }

    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/rounds/${selectedPeriodKey}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setPeriodMeta(d); })
      .catch(() => {});

    loadContributions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriodKey, sessionId, currentUserId, config.bbType]);

  // Load synthesis. Prefer the synthesis_summary pointer, but fall back to the last
  // entry of synthesisReports[] — some rounds have a report in the history array
  // without the summary pointer set, which otherwise leaves the TAR unloadable on
  // reload even though Studio (which reads the reports directly) still shows it.
  useEffect(() => {
    const reports = periodMeta?.synthesisReports;
    const reportId = periodMeta?.synthesisSummary?.reportId
      ?? (reports && reports.length > 0 ? reports[reports.length - 1].reportId : undefined);
    if (!reportId) return;
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/synthesis/${reportId}`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setSynthesis(d); })
      .catch(() => {});
  }, [sessionId, periodMeta?.synthesisSummary?.reportId, periodMeta?.synthesisReports, config.bbType]);

  // Navigation
  const selectedIndex = allPeriods.findIndex((q) => q.period_key === selectedPeriodKey);
  const canGoPrev = selectedIndex > 0;
  const canGoNext = selectedIndex < allPeriods.length - 1;

  // Actions
  const doSynthesis = async () => {
    if (!selectedPeriodKey) return;
    setIsSynthesizing(true);
    setSynthStream("");
    setSynthError(null);
    try {
      const response = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/synthesize/${selectedPeriodKey}`,
        { method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ committedOnly }) }
      );
      if (!response.ok) {
        setSynthError(`Synthesis failed (${response.status})`);
        setIsSynthesizing(false);
        return;
      }
      if (!response.body) { setSynthError("No response body"); setIsSynthesizing(false); return; }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let done = false;
      let reportId = "";
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.error) { setSynthError(payload.error); continue; }
            if (payload.content) setSynthStream((prev) => prev + payload.content);
            if (payload.done) {
              reportId = payload.reportId;
              const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/rounds/${selectedPeriodKey}`, { credentials: "include" });
              if (r.ok) setPeriodMeta(await r.json());
              if (reportId) {
                const sr = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/synthesis/${reportId}`, { credentials: "include" });
                if (sr.ok) setSynthesis(await sr.json());
              }
              setVersionsRefreshKey((k) => k + 1);
              loadContributions();
              onSessionRefresh?.();
              onReportCreated?.();
            }
          } catch (parseErr) { console.warn("SSE parse error:", parseErr); }
        }
      }
    } catch (err) {
      setSynthError(`Synthesis request failed: ${err instanceof Error ? err.message : "Network error"}`);
    } finally { setIsSynthesizing(false); }
  };

  const handleTriggerSynthesis = () => {
    setSynthConfirmOpen(true);
  };

  const handleLockToggle = async () => {
    if (!selectedPeriodKey) return;
    setLocking(true);
    try {
      const endpoint = isLocked ? "unlock" : "lock";
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/${endpoint}/${selectedPeriodKey}`,
        { method: "POST", credentials: "include" }
      );
      if (r.ok) {
        const newLocked = !isLocked;
        setIsLocked(newLocked);
        const pi = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/period-info`, { credentials: "include" });
        if (pi.ok) { const d = await pi.json(); setAllPeriods(d.allQuarters || []); }
        if (newLocked) {
          const obj = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/objectives/${selectedPeriodKey}`, { credentials: "include" });
          if (obj.ok) setLockedObjectives(await obj.json());
        }
        onLockChange?.();
      }
    } catch { /* ignore */ }
    finally { setLocking(false); }
  };

  const lockedPeriodKeys = allPeriods.filter((q) => q.is_locked).map((q) => q.period_key);

  const handleVisibilityToggle = async () => {
    if (!selectedPeriodKey) return;
    const mapKey = `${config.periodType}:${selectedPeriodKey}`;
    const effectiveVis = isVisibleForPeriod(visMap, config.periodType, selectedPeriodKey, { lockedQuarterKeys: lockedPeriodKeys });
    const newVis = effectiveVis ? "gated" : "open";
    const updatedMap = { ...visMap, [mapKey]: newVis as "open" | "gated" };
    setVisMap(updatedMap);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period_type: config.periodType, period_key: selectedPeriodKey, visibility: newVis }),
      });
      if (r.ok) { const serverMap = await r.json(); setVisMap(serverMap); onVisibilityChange?.(serverMap); }
    } catch { /* ignore */ }
  };

  // Derived state. The per-block toggle below decides which slice the
  // tab reads from — Solo reads viewerOutput.buildingBlocks, Team reads
  // the parent session's consensus. Solo falls back to team if the
  // viewer has no roadmap_outputs row yet (legacy data).
  const effectiveViewMode: "solo" | "team" = propViewMode ?? "solo";
  const activeBuildingBlocks: AllBuildingBlocks =
    effectiveViewMode === "solo"
      ? (soloBuildingBlocks ?? buildingBlocks)
      : buildingBlocks;
  const alignmentBlock = config.alignmentBlockKey ? activeBuildingBlocks[config.alignmentBlockKey] : null;
  const alignmentItems = normalizeWmbtItems((alignmentBlock && "wmbt" in alignmentBlock) ? (alignmentBlock as any).wmbt || [] : []);
  const visibilityOpen = selectedPeriodKey ? isVisibleForPeriod(visMap, config.periodType, selectedPeriodKey, { lockedQuarterKeys: lockedPeriodKeys }) : false;
  const currentBlock = activeBuildingBlocks[config.blockKey];
  const currentWmbt = normalizeWmbtItems((currentBlock && "wmbt" in currentBlock) ? (currentBlock as any).wmbt || [] : []);

  let periodStatus: "locked" | "active" | "planning" | null = null;
  if (isLocked) {
    periodStatus = "locked";
  } else {
    const lockedKeys = allPeriods.filter((q) => q.is_locked).map((q) => q.period_key).sort();
    const lastLockedKey = lockedKeys.length > 0 ? lockedKeys[lockedKeys.length - 1] : null;
    if (lastLockedKey) {
      const lastLockedIdx = allPeriods.findIndex((q) => q.period_key === lastLockedKey);
      const activeKey = lastLockedIdx >= 0 && lastLockedIdx + 1 < allPeriods.length ? allPeriods[lastLockedIdx + 1].period_key : null;
      const planningKey = lastLockedIdx >= 0 && lastLockedIdx + 2 < allPeriods.length ? allPeriods[lastLockedIdx + 2].period_key : null;
      if (selectedPeriodKey === activeKey) periodStatus = "active";
      else if (selectedPeriodKey === planningKey) periodStatus = "planning";
    } else {
      const currentP = allPeriods.find((q) => q.is_current_quarter);
      if (currentP) {
        const currentIdx = allPeriods.indexOf(currentP);
        const planningKey = currentIdx + 1 < allPeriods.length ? allPeriods[currentIdx + 1].period_key : null;
        if (selectedPeriodKey === currentP.period_key) periodStatus = "active";
        else if (selectedPeriodKey === planningKey) periodStatus = "planning";
      }
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!selectedPeriodKey) {
    return (
      <div className="flex items-center justify-center h-24">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Team-only controls only render in State 2 (2+ participants, showViewToggle
  // true). In State 1 the config is empty so ConsensusBuilderBar suppresses them.
  const commandCenterConfig: CommandCenterConfig = showViewToggle ? {
    visibility: {
      isVisible: visibilityOpen,
      onToggle: handleVisibilityToggle,
      tooltipVisible: `Team data for ${config.periodLabelFn(selectedPeriodKey)} visible to contributors — click to hide`,
      tooltipHidden: `Team data for ${config.periodLabelFn(selectedPeriodKey)} hidden from contributors — click to show`,
    },
    // Notify + Synthesize act on the TEAM version, and the synthesized draft only
    // renders in Team view — offer them in Team view only (never synthesize from Solo
    // and see nothing).
    ...(effectiveViewMode === "team" ? {
      notify: { onClick: () => setNotifyOpen(true) },
      synthesize: { onClick: handleTriggerSynthesis, isSynthesizing },
    } : {}),
  } : {};

  const multiPeriod = allPeriods.length > 1;

  const periodNav = (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1">
        {multiPeriod && (
          <Tooltip title={canGoPrev ? `Go to ${config.periodLabelFn(allPeriods[selectedIndex - 1]?.period_key)}` : "No earlier periods"}>
            <Button type="text" size="small" icon={<ChevronLeft className="h-3.5 w-3.5" />} onClick={() => canGoPrev && setSelectedPeriodKey(allPeriods[selectedIndex - 1].period_key)} disabled={!canGoPrev} style={{ padding: "0 4px" }} />
          </Tooltip>
        )}
        <span className="text-[12px] font-semibold text-primary px-1">{config.periodLabelFn(selectedPeriodKey)}</span>
        {isAdvisorOrOwner && (
          <Tooltip title={isLocked ? "Click to unlock" : "Click to lock"}>
            <button
              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border-0 outline-none cursor-pointer transition-all shrink-0 ${isLocked ? "bg-amber-100 hover:bg-amber-200" : "bg-transparent hover:bg-slate-100"}`}
              onClick={handleLockToggle}
              disabled={locking}
            >
              {isLocked ? <Lock className="h-3 w-3 text-amber-700" /> : <Unlock className="h-3 w-3 text-slate-400" />}
            </button>
          </Tooltip>
        )}
        {!isAdvisorOrOwner && isLocked && <Lock className="h-3 w-3 text-amber-600 shrink-0" />}
        {multiPeriod && periodStatus === "active" && <Tag color="blue" className="text-[10px] py-0 shrink-0">Active</Tag>}
        {multiPeriod && periodStatus === "planning" && <Tag className="text-[10px] py-0 shrink-0" style={{ color: "#8c8c8c", borderColor: "#d9d9d9", background: "#fafafa" }}>Planning Ahead</Tag>}
        {multiPeriod && (
          <Tooltip title={canGoNext ? `Go to ${config.periodLabelFn(allPeriods[selectedIndex + 1]?.period_key)}` : "No later periods"}>
            <Button type="text" size="small" icon={<ChevronRight className="h-3.5 w-3.5" />} onClick={() => canGoNext && setSelectedPeriodKey(allPeriods[selectedIndex + 1].period_key)} disabled={!canGoNext} style={{ padding: "0 4px" }} />
          </Tooltip>
        )}
      </div>
      {multiPeriod && (
        <div className="flex items-center gap-1">
          {allPeriods.map((q) => {
            const lockedKeys = allPeriods.filter((x) => x.is_locked).map((x) => x.period_key).sort();
            const lastLK = lockedKeys.length > 0 ? lockedKeys[lockedKeys.length - 1] : null;
            const lastLKIdx = lastLK ? allPeriods.findIndex((x) => x.period_key === lastLK) : -1;
            const aqKey = lastLKIdx >= 0 && lastLKIdx + 1 < allPeriods.length ? allPeriods[lastLKIdx + 1].period_key : null;
            const isActiveQ = aqKey ? q.period_key === aqKey : q.is_current_quarter;
            let dotColor = "#cbd5e1";
            if (q.period_key === selectedPeriodKey) dotColor = "#2c1561";
            else if (q.is_locked) dotColor = "#f59e0b";
            else if (isActiveQ) dotColor = "#1677ff";
            return (
              <Tooltip key={q.period_key} title={config.periodLabelFn(q.period_key)}>
                <button onClick={() => setSelectedPeriodKey(q.period_key)} style={{ width: 6, height: 6, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0, background: dotColor }} />
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      <ConsensusBuilderBar
        isAdvisorOrOwner={isAdvisorOrOwner}
        showToggle={false}
        controls={commandCenterConfig}
        leftContent={periodNav}
      />

      <div className="overflow-y-auto px-3 py-3 space-y-4 flex-1">

      {/* Team Alignment Report — rendered inline whenever a synthesis exists for
          this period, in BOTH Solo and Team view (the TAR is shared team output).
          Previously it lived only in the team branch, so it vanished on reload when
          the block sat in the default Solo view. Still gated so contributors only
          see it once the owner shares team data (visibilityOpen); owner/advisor
          always. */}
      {synthesis && !isSynthesizing && (isAdvisorOrOwner || visibilityOpen) && (
        <SynthesisSection
          synthesis={synthesis}
          bbLabel={config.bbLabel}
          periodLabel={config.periodLabelFn(selectedPeriodKey)}
          onDownload={() => {
            const pLabel = config.periodLabelFn(selectedPeriodKey);
            exportTarPdf(synthesis.reportMarkdown, {
              companyName: companyName || "Roadmap Session",
              periodLabel: `${config.bbLabel} — ${pLabel}`,
              generatedAt: synthesis.generatedAt,
            });
          }}
          onDelete={isAdvisorOrOwner ? async () => {
            try {
              await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/alignment-reports/${synthesis.id}`, { method: "DELETE", credentials: "include" });
              setSynthesis(null);
              const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/rounds/${selectedPeriodKey}`, { credentials: "include" });
              if (r.ok) {
                const rd = await r.json();
                setPeriodMeta(rd);
                const reports = rd.synthesisReports || [];
                if (reports.length > 0) {
                  const lastId = reports[reports.length - 1].reportId;
                  const sr = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/synthesis/${lastId}`, { credentials: "include" });
                  if (sr.ok) setSynthesis(await sr.json());
                }
              }
              antdMessage.success("Report deleted");
              onReportCreated?.();
            } catch { antdMessage.error("Failed to delete report"); }
          } : undefined}
        />
      )}

      {/* LOCKED STATE */}
      {isLocked ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-600">
            <Lock className="h-3 w-3" />
            Objectives locked — finalized for {config.periodLabelFn(selectedPeriodKey)}
          </div>
          {(lockedObjectives?.wmbt || []).length > 0 ? (
            <div className="space-y-2">
              {(lockedObjectives!.wmbt as WmbtProposal[]).map((item, i) => (
                <ObjectiveCard key={i} item={item} index={i} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/60 italic">No objectives recorded for this {config.periodNoun}.</p>
          )}
        </div>
      ) : effectiveViewMode === "solo" ? (
        <>
          {alignmentItems.length > 0 && <AlignmentContext items={alignmentItems} label={config.alignmentLabel} />}
          <MyContributionForm
            key={selectedPeriodKey}
            config={config}
            sessionId={sessionId}
            periodKey={selectedPeriodKey}
            contribution={myContribution}
            fallbackWmbt={currentWmbt as WMBT[]}
            autoEdit={autoEdit}
            onSendToMainChat={onSendToMainChat}
            onSaved={(saved) => {
              setMyContribution(saved);
              setAllContributions((prev) => {
                const idx = prev.findIndex((d) => d.contributorUserId === saved.contributorUserId);
                return idx >= 0 ? prev.map((d, i) => i === idx ? saved : d) : [...prev, saved];
              });
            }}
          />
          <VersionedSoloObjectives
            key={`vso-${selectedPeriodKey}-${versionsRefreshKey}`}
            config={config}
            sessionId={sessionId}
            periodKey={selectedPeriodKey}
            onSessionRefresh={onSessionRefresh}
            refreshKey={versionsRefreshKey}
            buildingBlocks={activeBuildingBlocks}
            fallbackWmbt={currentWmbt as WmbtProposal[]}
          />
        </>
      ) : (
        <>
          {!isAdvisorOrOwner && !visibilityOpen ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
              <EyeOff className="h-6 w-6 text-muted-foreground/40" />
              <p className="text-sm font-medium text-foreground/60">Team data is not yet available</p>
              <p className="text-xs text-muted-foreground/50 max-w-xs">The advisor will share team contributions and synthesized insights when ready.</p>
            </div>
          ) : (
            <>
              {alignmentItems.length > 0 && <AlignmentContext items={alignmentItems} label={config.alignmentLabel} />}
              {(() => {
                const visible = isAdvisorOrOwner ? allContributions : allContributions.filter((c) => c.contributorUserId === currentUserId);
                return (
                  <CollapsibleSection
                    title={isAdvisorOrOwner ? `Team Contributions (${visible.length})` : "My Contribution"}
                    icon={<Users className="h-3 w-3" />}
                    defaultExpanded={false}
                  >
                    {visible.length > 0 ? (
                      <ContributorList contributions={visible} />
                    ) : (
                      <p className="text-xs text-muted-foreground/60 italic py-2">
                        {isAdvisorOrOwner
                          ? "No contributions yet. Team members can submit their proposed objectives from the Solo view."
                          : "You haven't submitted a contribution yet. Switch to Solo view to propose your objectives."}
                      </p>
                    )}
                  </CollapsibleSection>
                );
              })()}
              {synthError && (
                <div className="border border-red-200 rounded-md bg-red-50 p-3 text-xs text-red-700">
                  <p className="font-semibold mb-1">Synthesis Error</p>
                  <p>{synthError}</p>
                </div>
              )}
              {isSynthesizing && synthStream && (
                <div className="border border-violet-100 rounded-md bg-violet-50/20 p-3">
                  <p className="text-[10px] font-semibold text-violet-600 mb-1.5 flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Vo is synthesizing…
                  </p>
                  <div className="text-xs text-foreground/70 leading-relaxed">
                    {renderMarkdown(synthStream, { baseFontSize: 11 })}
                  </div>
                </div>
              )}
              {!synthesis && !isSynthesizing && !synthError && (
                <p className="text-xs text-muted-foreground/60 italic">
                  {isAdvisorOrOwner
                    ? `No synthesis yet. Use "Synthesize with Vo" to generate team objectives from contributions.`
                    : "Team synthesis not yet available."}
                </p>
              )}
              {isAdvisorOrOwner && (
                <CollapsibleSection title="Work with Vo on Team Objectives" icon={<Sparkles className="h-3 w-3" />} variant="accent" defaultExpanded>
                  <TeamDraftEditForm
                    key={`team-edit-${selectedPeriodKey}`}
                    config={config}
                    sessionId={sessionId}
                    periodKey={selectedPeriodKey}
                    onSendToMainChat={onSendToMainChat}
                    onSaved={() => {
                      setVersionsRefreshKey((k) => k + 1);
                      fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/bb/${config.bbType}/rounds/${selectedPeriodKey}`, { credentials: "include" })
                        .then((r) => r.ok ? r.json() : null)
                        .then((rd) => { if (rd) setPeriodMeta(rd); });
                      onSessionRefresh?.();
                      antdMessage.success("Team draft updated");
                    }}
                  />
                </CollapsibleSection>
              )}
              <VersionedTeamObjectives
                key={`vto-${selectedPeriodKey}-${versionsRefreshKey}`}
                config={config}
                sessionId={sessionId}
                periodKey={selectedPeriodKey}
                isAdvisorOrOwner={isAdvisorOrOwner}
                onSessionRefresh={onSessionRefresh}
                fallbackWmbt={currentWmbt as WmbtProposal[]}
              />
              {isAdvisorOrOwner && (
                <div className="border-t border-slate-100 pt-4 mt-2">
                  <Button
                    block
                    onClick={handleLockToggle}
                    loading={locking}
                    icon={isLocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    style={{
                      height: 40,
                      fontWeight: 600,
                      fontSize: 13,
                      borderRadius: 8,
                      ...(isLocked
                        ? { borderColor: "#d97706", color: "#92400e", background: "#fffbeb" }
                        : { borderColor: "#2c1561", color: "#2c1561", background: "#f5f3ff" }),
                    }}
                  >
                    {isLocked
                      ? `Unlock ${config.periodLabelFn(selectedPeriodKey)}`
                      : `Lock ${config.periodLabelFn(selectedPeriodKey)} — Finalize Team ${config.bbLabel}`}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <NotifyModal open={notifyOpen} onClose={() => setNotifyOpen(false)} config={config} sessionId={sessionId} periodKey={selectedPeriodKey} />
      <Modal
        open={synthConfirmOpen}
        onCancel={() => setSynthConfirmOpen(false)}
        title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" /> Synthesize with Vo</span>}
        okText="Run Synthesis"
        onOk={() => { setSynthConfirmOpen(false); doSynthesis(); }}
      >
        <p className="text-sm text-foreground/70">
          Vo will analyze all contributions and create a <strong>new version</strong> of Team {config.bbLabel}. The current version will be preserved in version history.
        </p>
        <div className="mt-4 pt-3 border-t border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            What should Vo synthesize?
          </div>
          <Radio.Group
            value={committedOnly}
            onChange={(e) => setCommittedOnly(e.target.value)}
            className="flex flex-col gap-1.5"
          >
            <Radio value={false}>
              <span className="text-sm font-medium">All contributions</span>
              <span className="text-xs text-muted-foreground ml-1.5">drafts + committed</span>
            </Radio>
            <Radio value={true}>
              <span className="text-sm font-medium">Committed views only</span>
              <span className="text-xs text-muted-foreground ml-1.5">views a contributor marked ready</span>
            </Radio>
          </Radio.Group>
        </div>
      </Modal>
      </div>
    </div>
  );
}
